import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './LeadActivityComposer.module.css';
import { ACTIVITY_TYPES, ACTIVITY_ICONS } from './leadActivityIcons';

const INPUT_WRAP_CLASS = {
  note: styles.composerInputWrapNote,
  call: styles.composerInputWrapCall,
  email: styles.composerInputWrapEmail,
};

const MIN_TEXTAREA_ROWS = 1;
const LINE_HEIGHT_PX = 22;

export const LeadActivityComposer = ({
  activityType,
  activityBody,
  onTypeChange,
  onBodyChange,
  onSubmit,
  saving = false,
  t,
}) => {
  const textareaRef = useRef(null);
  const [noteErrorShown, setNoteErrorShown] = useState(false);
  const minHeightPx = MIN_TEXTAREA_ROWS * LINE_HEIGHT_PX;
  const trimmedBody = activityBody.trim();
  const noteRequiresBody = activityType === 'note' && !trimmedBody;
  const showNoteError = noteErrorShown && noteRequiresBody;

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.max(el.scrollHeight, minHeightPx);
    el.style.height = `${next}px`;
  }, [minHeightPx]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [activityBody, adjustTextareaHeight]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (saving) return;
    if (noteRequiresBody) {
      setNoteErrorShown(true);
      return;
    }
    onSubmit?.(activityType, trimmedBody);
  };

  const handleBodyChange = (value) => {
    if (value.trim()) {
      setNoteErrorShown(false);
    }
    onBodyChange(value);
  };

  const handleTypeChange = (type) => {
    if (type !== 'note') {
      setNoteErrorShown(false);
    }
    onTypeChange(type);
  };

  const handleBlur = () => {
    if (activityType === 'note' && !trimmedBody) {
      setNoteErrorShown(true);
    }
  };

  const placeholder = t(`leads.activityPlaceholder.${activityType}`);

  return (
    <form
      className={`${styles.composerForm} leads-activity-composer`}
      onSubmit={handleSubmit}
    >
      <div
        className={`${styles.composerInputWrap} ${INPUT_WRAP_CLASS[activityType] || ''}${
          showNoteError ? ` ${styles.composerInputWrapError}` : ''
        }`}
      >
        <textarea
          ref={textareaRef}
          className={`${styles.composerTextarea} leads-activity-composer__textarea`}
          placeholder={placeholder}
          value={activityBody}
          onChange={(e) => handleBodyChange(e.target.value)}
          onInput={adjustTextareaHeight}
          onBlur={handleBlur}
          rows={MIN_TEXTAREA_ROWS}
          aria-label={placeholder}
          aria-invalid={showNoteError}
          disabled={saving}
        />
        <div className={styles.composerFooter}>
          <div className={styles.composerActions}>
            {ACTIVITY_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`${styles.typeIconBtn} leads-activity-composer__type-btn${
                  activityType === type ? ` ${styles.typeIconBtnSelected}` : ''
                }`}
                onClick={() => handleTypeChange(type)}
                aria-label={t(`leads.activityType.${type}`)}
                aria-pressed={activityType === type}
                disabled={saving}
              >
                <i className={ACTIVITY_ICONS[type]} aria-hidden="true" />
              </button>
            ))}
            <button
              type="submit"
              className={`${styles.sendBtn} leads-activity-composer__send-btn`}
              aria-label={t('leads.addActivity')}
              disabled={saving}
            >
              <i className="fa fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      {showNoteError && (
        <p className={styles.composerError} role="alert">
          {t('leads.activityNoteRequired')}
        </p>
      )}
    </form>
  );
};
